/*
 * pfc-photo-source.js — the customer's ORIGINAL photo, uploaded untouched.
 *
 * Shared by pfc-print-app.js, pfc-mount.js and pfc-moulding.js (load it before
 * them). The customisers used to cut the crop in the browser and upload only
 * that — a canvas re-encode, capped at 2400-4000px, the real file never leaving
 * the tab. When a crop went wrong there was nothing to recover (PFC-UK-1338458).
 *
 * Now:
 *   - the picked File is PUT to S3 byte-for-byte the moment it is chosen, in the
 *     background, while the customer carries on choosing frame / mount / size;
 *   - the crop travels as a box (`_photo_crop`), and the print master is cut from
 *     the original on the print-files page (dropshipping-project
 *     src/lib/services/photo-crop.ts);
 *   - add-to-cart stays disabled until the upload has finished, so an order can
 *     never reference an original that is not there.
 *
 * One upload per PHOTO, not per crop: recropping, flipping orientation or
 * changing size only changes the box. Picking another file starts a new upload
 * under a new id straight away. It is provisional until the customer confirms
 * the crop: commit() keeps it, revert() (cropper cancelled) goes back to the
 * previous photo, so the upload can never belong to a different photo from the
 * crop on screen. Originals that never reach an order are removed by the
 * platform's print-src sweep after 60 days.
 *
 * Crop box coordinates are natural pixels in the DISPLAYED orientation (EXIF
 * applied), which is what Cropper.js getData() returns. Do not send its `rotate`:
 * for an EXIF-oriented photo that is Cropper's own compensation, not a customer
 * rotation, and the server would rotate the print twice.
 */
(function () {
  'use strict';

  var MAX_BYTES = 50 * 1024 * 1024;
  var MAX_ATTEMPTS = 3;

  function rid() {
    try {
      var a = new Uint8Array(16); crypto.getRandomValues(a);
      return Array.from(a).map(function (b) { return b.toString(16).padStart(2, '0'); }).join('');
    } catch (_) { return String(Date.now()) + Math.random().toString(16).slice(2); }
  }

  function extFor(file) {
    var t = (file.type || '').toLowerCase();
    if (t === 'image/jpeg' || t === 'image/jpg') return 'jpg';
    if (t === 'image/png') return 'png';
    if (t === 'image/heic' || t === 'image/heif') return 'heic';
    if (t === 'image/webp') return 'webp';
    var m = /\.([a-z0-9]{2,5})$/i.exec(file.name || '');
    return m ? m[1].toLowerCase() : 'bin';
  }

  /*
   * Crop spec wire format: "x,y,w,h/WxH". Integers, clamped inside the image —
   * the server refuses a box that runs past the edge, and a recropped box is
   * fractional.
   */
  function cropSpec(box, srcW, srcH) {
    var W = Math.round(srcW), H = Math.round(srcH);
    if (!(W > 0 && H > 0)) return '';
    if (!box) return '0,0,' + W + ',' + H + '/' + W + 'x' + H;   // no crop = whole image
    var x = Math.max(0, Math.min(W - 1, Math.round(box.x || 0)));
    var y = Math.max(0, Math.min(H - 1, Math.round(box.y || 0)));
    var w = Math.max(1, Math.min(W - x, Math.round(box.width)));
    var h = Math.max(1, Math.min(H - y, Math.round(box.height)));
    return x + ',' + y + ',' + w + ',' + h + '/' + W + 'x' + H;
  }

  function create(opts) {
    var st = { status: 'idle', progress: 0, url: null, error: null };
    var file = null, key = null, xhr = null, timer = null;
    var gen = 0, attempt = 0;
    var prev = null;   // the confirmed photo a provisional start() replaced

    function emit() {
      if (!opts.onChange) return;
      try { opts.onChange(snapshot()); } catch (e) { console.error('PFC photo source listener', e); }
    }
    function snapshot() {
      return { status: st.status, progress: st.progress, url: st.url, error: st.error };
    }
    function stopTransfer() {
      gen++;
      if (timer) { clearTimeout(timer); timer = null; }
      if (xhr) { try { xhr.abort(); } catch (_) {} xhr = null; }
    }
    function fail(g, why) {
      if (g !== gen) return;
      if (attempt < MAX_ATTEMPTS) {
        timer = setTimeout(function () { timer = null; send(g); }, 1000 * attempt);
        return;
      }
      console.error('PFC photo upload failed', why);
      st.status = 'error'; st.error = String(why || 'upload failed'); emit();
    }
    function send(g) {
      if (g !== gen) return;
      attempt++;
      var form = new URLSearchParams();
      form.append('bucket', opts.bucket); form.append('key', key); form.append('type', 'put');
      fetch(opts.signerUrl, {
        method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: form,
      })
        .then(function (r) { if (!r.ok) throw new Error('signer ' + r.status); return r.text(); })
        .then(function (signedUrl) {
          if (g !== gen) return;
          var x = new XMLHttpRequest();
          xhr = x;
          x.open('PUT', signedUrl.trim());
          x.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
          x.upload.onprogress = function (e) {
            if (g !== gen || !e.lengthComputable) return;
            // Hold at 99% until S3 answers — 100% means "stored", not "sent".
            st.progress = Math.min(0.99, e.loaded / e.total); emit();
          };
          x.onload = function () {
            if (g !== gen) return;
            xhr = null;
            if (x.status >= 200 && x.status < 300) {
              st.status = 'done'; st.progress = 1; st.error = null; emit();
            } else fail(g, 'S3 ' + x.status);
          };
          x.onerror = function () { if (g === gen) { xhr = null; fail(g, 'network'); } };
          x.send(file);
        })
        .catch(function (e) { fail(g, e && e.message); });
    }

    /* Begin uploading a newly picked file. False when it is over the size limit:
     * nothing changes, and the caller should not open the cropper. */
    function start(f) {
      if (f && f.size > MAX_BYTES) return false;
      // Remember the confirmed photo (only the first provisional replace — a second
      // pick before confirming still reverts to the last CONFIRMED one).
      if (!prev) prev = { file: file, key: key, st: snapshot() };
      stopTransfer();
      file = null; key = null;
      st = { status: 'idle', progress: 0, url: null, error: null };
      if (!f) { emit(); return false; }
      var d = new Date();
      var ym = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
      file = f;
      key = (opts.prefix || 'print-src') + '/' + ym + '/' + rid() + '/original.' + extFor(f);
      st.url = 'https://' + opts.bucket + '.s3.eu-west-2.amazonaws.com/' + key;
      st.status = 'uploading';
      attempt = 0;
      emit();
      send(gen);
      return true;
    }
    /* The crop for the provisional photo was confirmed — it is now the photo. */
    function commit() { prev = null; }

    /* The cropper was cancelled: go back to the photo that was confirmed before.
     * Resumes its upload if it had not finished. */
    function revert() {
      if (!prev) return;
      var p = prev; prev = null;
      stopTransfer();
      file = p.file; key = p.key;
      st = { status: p.st.status, progress: p.st.progress, url: p.st.url, error: p.st.error };
      if (file && st.status !== 'done') { st.status = 'uploading'; st.progress = 0; st.error = null; attempt = 0; emit(); send(gen); return; }
      if (!file) st = { status: 'idle', progress: 0, url: null, error: null };
      emit();
    }

    /* Photo removed ("Add my photo" unticked). */
    function clear() {
      prev = null;
      stopTransfer();
      file = null; key = null;
      st = { status: 'idle', progress: 0, url: null, error: null };
      emit();
    }
    function retry() {
      if (st.status !== 'error' || !file) return;
      stopTransfer();
      st.status = 'uploading'; st.progress = 0; st.error = null; attempt = 0;
      emit();
      send(gen);
    }

    return {
      start: start, commit: commit, revert: revert, clear: clear, retry: retry, state: snapshot,
      busy: function () { return st.status === 'uploading'; },
      failed: function () { return st.status === 'error'; },
      ready: function () { return st.status === 'done' && !!st.url; },
    };
  }

  /*
   * Photo-upload messages as a toast, so they are seen wherever the customer is
   * on the page. The customisers' own status line sits by add-to-cart, usually
   * off-screen while a photo is being picked. One toast, reused:
   *   'busy'  stays up until the next notice (conversion in progress)
   *   'info'  4s, 'error' 7s; an empty message hides it.
   */
  var toastEl = null, toastTimer = null;
  function notice(msg, kind) {
    if (toastTimer) { clearTimeout(toastTimer); toastTimer = null; }
    if (!msg) {
      if (toastEl) toastEl.classList.remove('pfc-moulding__toast--in');
      return;
    }
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'pfc-moulding__toast';
      document.body.appendChild(toastEl);
    }
    toastEl.setAttribute('role', kind === 'error' ? 'alert' : 'status');
    toastEl.classList.toggle('pfc-moulding__toast--error', kind === 'error');
    toastEl.textContent = msg;
    requestAnimationFrame(function () { toastEl.classList.add('pfc-moulding__toast--in'); });
    if (kind !== 'busy') {
      toastTimer = setTimeout(function () { notice(''); }, kind === 'error' ? 7000 : 4000);
    }
  }

  window.PFCPhotoSource = { create: create, cropSpec: cropSpec, notice: notice, MAX_BYTES: MAX_BYTES };
})();

